import { useContext, useEffect, useState } from 'react';
import { previewIssueOptions, queryKeys } from '@shared/api';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { AppContext } from '@/app.context';
import { Issue, USER_ROLE } from '@/constants';
import config from '@/config';
import { useIssueNotFound } from '@shared/lib/hooks';

/**
 * React hook that returns the details of the issue
 *
 * @param id - Issue identifier
 *
 */
export function useGetIssue(id: string): GetIssueResponse {
	const queryClient = useQueryClient();
	const isAdmin = localStorage.getItem(USER_ROLE) === 'admin';
	const { onlineStatus: isOnline } = useContext(AppContext);
	const [issue, setIssue] = useState<Issue | null | undefined>();
	const queryKey = queryKeys.preview(id);

	// Same synchronous check `useIssue` (pages/issue/model/issue.hooks.ts)
	// uses -- computed independently of the query below, purely to drive
	// the "unavailable offline" UI branch. `previewIssueOptions`'s own
	// queryFn (shared/api/queries.ts) already resolves to this same cached
	// value (or `{ issue: null }`) without a network attempt when offline,
	// so nothing is lost by not also gating the query with `enabled`.
	const isUnavailableOffline =
		!isOnline && queryClient.getQueryData(queryKey) === undefined;

	// `previewIssueOptions` -- the same factory `previewViewRoute`'s own
	// `loader` calls via `ensureQueryData` (route-catalog.tsx) -- so a
	// reader who hovered an issue cover before clicking (this route's
	// `defaultPreload: 'intent'`) already has this exact query warmed by
	// the time this hook runs, and this just picks up that same cached/
	// in-flight result instead of starting a second request.
	const { isPending, error, data } = useQuery({
		...previewIssueOptions(id, {
			isOnline,
			isAdmin,
			ttlMinutes: config.app?.cache?.ttl?.getIssue,
		}),
	});

	useIssueNotFound(issue);

	useEffect(() => {
		if (!data) return;
		setIssue(data.issue);
	}, [data]);

	return {
		isLoading: isUnavailableOffline ? false : isPending,
		isUnavailableOffline,
		error: error ? new Error(error.message) : null,
		issue,
	};
}

export interface GetIssueResponse {
	issue: Issue | null | undefined;
	isLoading: boolean;
	isUnavailableOffline: boolean;
	error?: Error | null;
}
